import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    FormHelperText,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import AppDialogTitle from '../../ui/appDialogTitle';
import ProvinceSelect from '../../common/provinceSelect';
import CitySelect from '../../common/citySelect';
import { formatFileSize } from '../../../utils';
import AttachmentUploader from '../../common/attachmentUploader';
import { LocationService } from '../../../service/locationService';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../utils/environment';
import locationValidationSchema from './locationValidationSchema';
import { AttachmentService } from '../../../service/attachmentService';
import { Colors } from '../../../styles/theme';
import { TIMEZONE_OPTIONS } from '../../../utils/timezones';
import {
    COUNTRY_OPTIONS,
    getCountryId,
    normalizeCountryValue
} from '../../../utils/preferencesOptions';

const MAX_FILE_SIZE = 1 * 1024 * 1024
const MAX_NO_OF_FILES = 1
const ACCEPTED_IMAGE_FILE_FORMAT = ['image/jpeg', 'image/png', 'image/jpg']

// country will be listed under personalization
const initialValues = {
    addrNo: '',
    addrUnit: '',
    addrStreet: '',
    addrCity: '',
    addrProvince: '',
    addrPostal: '',
    addrCountry: 'Canada',
    website: '',
    googleReviewLink: '',
    name: '',
    storePrefix: '',
    phone: '',
    fax: '',
    email: '',
    timezone: 'America/New_York',
    status: '',
    logo: ''
};

export default function LocationEditForm({ open, onClose, location, onLocationEdit }) {
    const [attachments, setAttachments] = useState([])


    useEffect(() => {
        if (location) {
            initialValues.id = location.id || -1;
            initialValues.addrNo = location.addrNo || '';
            initialValues.addrUnit = location.addrUnit || '';
            initialValues.addrStreet = location.addrStreet || '';
            initialValues.addrCity = location.addrCity || '';
            initialValues.addrProvince = location.addrProvince || '';
            initialValues.addrPostal = location.addrPostal || '';
            initialValues.addrCountry = normalizeCountryValue(location.addrCountry || 'Canada');
            initialValues.website = location.website || '';
            initialValues.googleReviewLink = location.googleReviewLink || '';
            initialValues.name = location.name || '';
            initialValues.storePrefix = location.storePrefix || '';
            initialValues.phone = location.phone || '';
            initialValues.fax = location.fax || '';
            initialValues.email = location.email || '';
            initialValues.status = location.status || '';
            initialValues.logo = location.logo || '';

            (async () => {
                const attachments = await AttachmentService.getAttachment('location', location.id)
                const { code, message, data } = attachments
                if (code === 0) {
                    setAttachments(data)
                }
            })()
        }
    }, [location])

    const handleSubmit = (values, { setSubmitting }) => {

        setSubmitting(true)

        LocationService.updateLocation(values, location.id).then(response => {
            const { code, message } = response;
            if (code === 0) {
                toast.success(message);
                onLocationEdit({
                    ...location,
                    ...values,
                    id: location.id,
                });
                onClose();
            }
            setSubmitting(false);
        }).catch(error => {
            if (isLocalDev) {
                console.log(error);
                setSubmitting(false);
            }

            toast.error('Failed to update record');
        });
    };

    return (
        <>
            <Dialog open={open} maxWidth={'md'}>
                <AppDialogTitle title={"Update Location"} onClose={onClose} />
                <DialogContent>
                    {/* <form onSubmit={formik.handleSubmit}> */}
                    <Formik
                        initialValues={initialValues}
                        validationSchema={locationValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ dirty, values, setFieldValue, isSubmitting }) => (

                            <Form>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.status === "active"}
                                            onChange={(e) =>
                                                setFieldValue("status", e.target.checked ? "active" : "inactive")
                                            } />
                                    }
                                    name='status'
                                    label={values.status === "active" ? <Typography sx={{ color: Colors.success }}>Active</Typography>
                                        : <Typography sx={{ color: Colors.danger }}>Inactive</Typography>}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <Field
                                            as={TextField}
                                            name="name"
                                            label="Name"
                                            size="small"
                                            required
                                            fullWidth
                                        />
                                        <FormHelperText>Choose a friendly name to help you choose the location across the app.</FormHelperText>
                                        <ErrorMessage name="name">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={6} mt={1}>
                                        <Field
                                            as={TextField}
                                            name="storePrefix"
                                            label="Store prefix"
                                            size="small"
                                            fullWidth
                                        />
                                        <FormHelperText>Optional. Used when generating invoices. Leave blank to auto-generate from location name.</FormHelperText>
                                        <ErrorMessage name="storePrefix">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Field
                                            as={TextField}
                                            name="addrNo"
                                            label="Number"
                                            size="small"
                                            required
                                            fullWidth
                                        />
                                        <ErrorMessage name="addrNo">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Field
                                            as={TextField}
                                            name="addrStreet"
                                            label="Street"
                                            size="small"
                                            required
                                            fullWidth
                                        />
                                        <ErrorMessage name="addrStreet">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Field
                                            as={TextField}
                                            name="addrUnit"
                                            label="Unit"
                                            size="small"
                                            fullWidth
                                        />
                                        <ErrorMessage name="addrUnit">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <Field
                                            name="addrCity"
                                            component={CitySelect}
                                            countryId={getCountryId(values.addrCountry)}
                                            label="City"
                                        />
                                        <ErrorMessage name="addrCity">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <Field
                                            name="addrProvince"
                                            component={ProvinceSelect}
                                            countryId={getCountryId(values.addrCountry)}
                                            label="Province"
                                        />
                                        <ErrorMessage name="addrProvince">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Field
                                            as={TextField}
                                            name="addrPostal"
                                            label="Postal code"
                                            size="small"
                                            required
                                            fullWidth
                                        />
                                        <ErrorMessage name="addrPostal">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small" required>
                                            <InputLabel>Country</InputLabel>
                                            <Field name="addrCountry">
                                                {({ field, form }) => (
                                                    <Select
                                                        {...field}
                                                        label="Country"
                                                        onChange={(e) => {
                                                            form.setFieldValue('addrCountry', e.target.value);
                                                            // Reset dependent region fields when country changes.
                                                            form.setFieldValue('addrCity', '');
                                                            form.setFieldValue('addrProvince', '');
                                                        }}
                                                    >
                                                        {COUNTRY_OPTIONS.map((country) => (
                                                            <MenuItem key={country.value} value={country.value}>
                                                                {country.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            </Field>
                                        </FormControl>
                                        <ErrorMessage name="addrCountry">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            name="phone"
                                            label="Phone"
                                            size="small"
                                            required
                                            fullWidth
                                        />
                                        <ErrorMessage name="phone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            name="fax"
                                            label="Fax"
                                            size="small"
                                            fullWidth
                                        />
                                        <ErrorMessage name="fax">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="email"
                                            label="Email"
                                            size="small"
                                            fullWidth
                                        />
                                        <ErrorMessage name="email">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small" required>
                                            <InputLabel>Timezone</InputLabel>
                                            <Field name="timezone">
                                                {({ field, form }) => (
                                                    <Select
                                                        {...field}
                                                        label="Timezone"
                                                        onChange={(e) => form.setFieldValue('timezone', e.target.value)}
                                                    >
                                                        {TIMEZONE_OPTIONS.map((tz) => (
                                                            <MenuItem key={tz.value} value={tz.value}>
                                                                {tz.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            </Field>
                                        </FormControl>
                                        <ErrorMessage name="timezone">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="website"
                                            label="Website"
                                            size="small"
                                            fullWidth
                                        />
                                        <ErrorMessage name="website">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="googleReviewLink"
                                            label="Review link"
                                            size="small"
                                            fullWidth
                                        />
                                        <FormHelperText>A link you can send in email or text for customers to leave a review.</FormHelperText>
                                        <ErrorMessage name="googleReviewLink">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant='caption'>Logo (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)}). Max files allowed {MAX_NO_OF_FILES}</Typography>
                                        <Field
                                            name="logo"
                                            values={values.logo}
                                            component={AttachmentUploader}
                                            onUpload={(files) => setFieldValue('logo', files[0])}
                                            accept={
                                                ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                                    acc[mimeType] = []
                                                    return acc
                                                }, {})
                                            }
                                            maxFiles={MAX_NO_OF_FILES}
                                            maxSize={MAX_FILE_SIZE}
                                            attachments={attachments}
                                        />
                                    </Grid>
                                    <Grid item xs={12} >
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={!dirty || isSubmitting}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            onClick={onClose}
                                            sx={{ ml: 2 }}
                                            variant="outlined"
                                            color="primary">
                                            Cancel
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                    {/* </form> */}
                </DialogContent>
            </Dialog>
        </>

    );
};